import { useMemo } from "react";
import useSWR from "swr";
import { LibertyAiAgentState, TypedDocumentString } from "../generated/graphql";
import graphQLFetcher from "../index";

// Hand-written rather than generated -- but the point of this file isn't
// codegen (see useDeleteLibertyAIConversation.ts for why that's broken
// here), it's that this query is deliberately narrower than the generated
// `LibertyAIConversations` one: no `messagesConnection`. That field is what
// made the sidebar's initial fetch pull *every* conversation's entire
// message history just to render a list of rows -- the root cause of a slow
// initial load. Use this for the conversation list, and
// useLibertyAIConversations(ids) scoped to just the active conversation for
// its messages.
export interface LibertyAiConversationSummary {
  __typename?: "LibertyAIConversation";
  id: string;
  createdAt: string;
  updatedAt: string;
  state: LibertyAiAgentState;
  statusMessage?: string | null;
  name?: string | null;
}

export interface LibertyAiConversationSummariesQuery {
  __typename?: "Query";
  libertyAIConversations: LibertyAiConversationSummary[];
}

const LibertyAiConversationSummariesDocument = new TypedDocumentString(`
    query LibertyAIConversationSummaries {
  libertyAIConversations {
    id
    createdAt
    updatedAt
    state
    statusMessage
    name
  }
}
    `) as unknown as TypedDocumentString<
  LibertyAiConversationSummariesQuery,
  Record<string, never>
>;

export interface UseLibertyAIConversationSummariesResponse {
  conversations: LibertyAiConversationSummary[] | undefined;
  loading: boolean;
  error: Error | undefined;
  /**
   * True while any fetch for the list is in flight -- including SWR's own
   * error retries and manual mutate() calls -- so a "Try again" control can
   * show progress. Unlike `loading`, this is also true when data exists.
   */
  revalidating: boolean;
  /**
   * Revalidates just the metadata list -- for a background (non-active)
   * conversation's notification, this is the cheap alternative to
   * useLibertyAIConversations' mergeConversation, which would pull that
   * conversation's full message history just to update a sidebar dot.
   */
  mutate: () => Promise<LibertyAiConversationSummariesQuery | undefined>;
}

export function useLibertyAIConversationSummaries(): UseLibertyAIConversationSummariesResponse {
  const { data, isLoading, isValidating, error, mutate } = useSWR(
    "libertyAIConversationSummaries",
    () => graphQLFetcher(LibertyAiConversationSummariesDocument, {}),
    {
      // Chat updates arrive via subscription; avoid extra traffic on tab focus.
      revalidateOnFocus: false,
      // But do refetch after the network comes back: a dropped connection is
      // the most common reason this list fails to load, and without this the
      // error banner would sit there until the user clicks "Try again".
      revalidateOnReconnect: true,
    },
  );

  return useMemo(
    () => ({
      conversations: data?.libertyAIConversations,
      loading: isLoading,
      error,
      revalidating: isValidating,
      mutate: () => mutate(),
    }),
    [data, error, isLoading, isValidating, mutate],
  );
}
